import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, X, Browser, MoneyWavy } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { sendBytes, b64ToBytes, runBrowserHandoffs } from "@/utils/webusbPrinter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// LocalStorage keys for printer selection.
// - DEFAULT: sticky "this terminal always uses this printer" pick set from
//   Settings → Equipment (star button). Highest precedence.
// - LAST: whichever printer was last actually printed to. Second choice.
const DEFAULT_PRINTER_KEY = "crest.receipt.defaultPrinterId";
const LAST_PRINTER_KEY = "crest.receipt.lastPrinterId";

const PrintableReceipt = ({ open, onClose, receipt }) => {
  const receiptRef = useRef(null);
  const [printers, setPrinters] = useState([]);
  const [selectedPrinterId, setSelectedPrinterId] = useState("");
  const [printing, setPrinting] = useState(false);
  const [loadingPrinters, setLoadingPrinters] = useState(false);

  // Fetch the available receipt printers each time the modal opens so an
  // installer who just added one doesn't need to reload the page.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingPrinters(true);
    axios
      .get(`${API}/printers/available`)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setPrinters(list);
        // Precedence: terminal default → last-used → first online → first.
        const explicitDefault = localStorage.getItem(DEFAULT_PRINTER_KEY);
        const last = localStorage.getItem(LAST_PRINTER_KEY);
        const stillDefault = list.find((p) => p.id === explicitDefault);
        const stillLast = list.find((p) => p.id === last);
        const online = list.find((p) => p.status === "online");
        setSelectedPrinterId(
          stillDefault?.id ||
          stillLast?.id ||
          online?.id ||
          list[0]?.id ||
          "",
        );
      })
      .catch(() => {
        if (!cancelled) setPrinters([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPrinters(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!receipt) return null;

  // Build the loyalty-signup URL with optional email/name prefill so
  // scanning the QR takes the guest straight to a half-filled form.
  const showLoyaltyQr = !receipt.is_loyalty_member;
  const loyaltySignupUrl = (() => {
    const base = `${window.location.origin}/loyalty-signup`;
    const params = new URLSearchParams();
    if (receipt.customer_email) params.set("email", receipt.customer_email);
    if (receipt.customer_name) params.set("name", receipt.customer_name);
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  })();

  // Direct hardware print — bytes go straight to the ESC/POS printer,
  // bypassing window.print() and both browser dialogs. When
  // ``kickDrawer`` is true, we also fire the cash-drawer pulse so a
  // single tap prints the customer receipt AND pops the till open.
  const handleHardwarePrint = async (kickDrawer = false) => {
    if (!selectedPrinterId) {
      toast.error("No printer selected");
      return;
    }
    setPrinting(true);
    try {
      // Fire both requests in parallel; the drawer pulse is best-effort
      // so a broken drawer never blocks the receipt from printing.
      const printReq = axios.post(
        `${API}/printers/${selectedPrinterId}/print-receipt`,
        { receipt },
      );
      const drawerReq = kickDrawer
        ? axios.post(`${API}/cash-drawer/open`, {
            reason: "receipt-print",
            order_id: receipt?.order_id,
            amount: receipt?.total_charged,
          })
        : null;

      const [printRes, drawerRes] = await Promise.allSettled([
        printReq,
        drawerReq || Promise.resolve(null),
      ]);

      if (printRes.status === "rejected") {
        const msg =
          printRes.reason?.response?.data?.detail ||
          printRes.reason?.message ||
          "Printer error — check that the device is online.";
        toast.error(msg);
        return;
      }

      // WebUSB handoff: backend rendered the ESC/POS bytes but couldn't
      // reach the terminal. Send them from the browser via the paired
      // USB device. This is what makes cloud POS printing to a plugged-in
      // USB thermal printer possible.
      const data = printRes.value?.data || {};
      if (data.browser_handoff && data.payload_b64) {
        try {
          await sendBytes(
            {
              vendorId: data.webusb_vendor_id,
              productId: data.webusb_product_id,
              serialNumber: data.webusb_serial_number,
            },
            b64ToBytes(data.payload_b64),
          );
        } catch (e) {
          toast.error(e?.message || "WebUSB print failed");
          return;
        }
      }

      localStorage.setItem(LAST_PRINTER_KEY, selectedPrinterId);
      // Fire any WebUSB drawer-kick handoffs from the browser side.
      let drawerWarn = "";
      if (kickDrawer && drawerRes.status === "fulfilled") {
        const { errors } = await runBrowserHandoffs(
          drawerRes.value?.data?.browser_handoff_results,
        );
        if (errors.length) drawerWarn = errors.join(" · ");
      }
      if (kickDrawer && drawerRes.status === "rejected") {
        toast.warning("Receipt printed — drawer kick failed");
      } else if (drawerWarn) {
        toast.warning(`Receipt printed — drawer: ${drawerWarn}`, { duration: 7000 });
      } else {
        toast.success(
          kickDrawer ? "Receipt printed + drawer opened" : "Receipt sent to printer",
        );
      }
      onClose?.();
    } finally {
      setPrinting(false);
    }
  };

  // Browser fallback — original behaviour, useful when no hardware printer
  // is configured or the operator wants a PDF/desktop-printer path.
  const handleBrowserPrint = () => {
    const content = receiptRef.current;
    if (!content) return;
    // Font-scale multipliers: "small" = 10px body, "normal" = 12px, "large" = 14px.
    const scale = (receipt.receipt_font_scale || "normal").toLowerCase();
    const bodySize = scale === "small" ? 10 : scale === "large" ? 14 : 12;
    const smSize = Math.max(8, bodySize - 2);
    const lgSize = bodySize + 4;
    const printWindow = window.open("", "_blank", "width=350,height=600");
    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&display=swap');
            @page { size: 80mm auto; margin: 0; }
            html, body { margin: 0; padding: 0; }
            body { font-family: 'Space Mono', monospace; font-size: ${bodySize}px; width: 80mm; padding: 4mm 3mm; box-sizing: border-box; }
            .center { text-align: center; }
            .line { border-top: 1px dashed #999; margin: 8px 0; }
            .row { display: flex; justify-content: space-between; margin: 3px 0; }
            .bold { font-weight: bold; }
            .lg { font-size: ${lgSize}px; }
            .sm { font-size: ${smSize}px; }
            .modifier { padding-left: 20px; color: #666; font-size: ${smSize}px; }
            .receipt-logo { max-width: 55mm; max-height: 20mm; margin: 0 auto 4px; display: block; }
            @media print {
              html, body { margin: 0; padding: 0; }
              body { padding: 3mm; }
            }
          </style>
        </head>
        <body>
          ${content.innerHTML}
          <script>window.onload = function() { window.print(); window.close(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleString();
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const hasPrinters = printers.length > 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-[380px] p-0 max-h-[85vh] flex flex-col gap-0"
        data-testid="receipt-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
          <DialogTitle className="font-heading text-xl">Receipt Preview</DialogTitle>
        </DialogHeader>

        {/* Scrollable receipt body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4" data-testid="receipt-scroll-area">
          <div
            ref={receiptRef}
            className={`bg-[#FFFDF7] border border-border rounded-lg p-6 font-mono ${
              (receipt.receipt_font_scale || "normal") === "small" ? "text-[10px]" :
              (receipt.receipt_font_scale || "normal") === "large" ? "text-sm" : "text-xs"
            }`}
            data-testid="receipt-content"
          >
            {/* Optional logo — controlled by Settings → Equipment → Receipt options */}
            {receipt.receipt_print_logo && receipt.logo_url && (
              <div className="center text-center mb-2">
                <img
                  src={receipt.logo_url}
                  alt="Store logo"
                  className="receipt-logo mx-auto"
                  style={{ maxWidth: "60%", maxHeight: 80 }}
                  data-testid="receipt-logo"
                />
              </div>
            )}
            {/* Header */}
            <div className="center text-center mb-4">
              <p className="lg bold text-base font-bold" data-testid="receipt-restaurant-name">
                {receipt.restaurant_name}
              </p>
              {receipt.address && (
                <p className="sm text-[10px] text-muted-foreground" data-testid="receipt-address-line-1">
                  {receipt.address}
                </p>
              )}
              {receipt.address_line_2 && (
                <p className="sm text-[10px] text-muted-foreground" data-testid="receipt-address-line-2">
                  {receipt.address_line_2}
                </p>
              )}
              {receipt.phone && (
                <p className="sm text-[10px] text-muted-foreground" data-testid="receipt-phone">
                  {receipt.phone}
                </p>
              )}
            </div>

            <div className="line border-t border-dashed border-border my-2"></div>

            {/* Order Info */}
            <div className="mb-3">
              <div className="row flex justify-between">
                <span>Order:</span>
                <span>#{receipt.order_id?.slice(-6)}</span>
              </div>
              <div className="row flex justify-between">
                <span>Table:</span>
                <span>{receipt.table_name}</span>
              </div>
              {receipt.customer_name && (
                <div className="row flex justify-between">
                  <span>Name:</span>
                  <span>{receipt.customer_name}</span>
                </div>
              )}
              <div className="row flex justify-between">
                <span>Server:</span>
                <span>{receipt.server_name}</span>
              </div>
              <div className="row flex justify-between">
                <span>Date:</span>
                <span>{formatDate(receipt.date)}</span>
              </div>
              <div className="row flex justify-between">
                <span>Guests:</span>
                <span>{receipt.guest_count}</span>
              </div>
            </div>

            <div className="line border-t border-dashed border-border my-2"></div>

            {/* Items */}
            <div className="mb-3">
              {receipt.items?.map((item, idx) => (
                <div key={idx} className="mb-1">
                  <div className="row flex justify-between">
                    <span>{item.quantity}x {item.menu_item_name}</span>
                    <span>${item.total_price?.toFixed(2)}</span>
                  </div>
                  {item.modifiers?.length > 0 && (
                    <div className="modifier pl-4 text-[10px] text-muted-foreground">
                      {item.modifiers.map(m => m.name).join(", ")}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="line border-t border-dashed border-border my-2"></div>

            {/* Totals */}
            <div className="mb-3">
              <div className="row flex justify-between">
                <span>Subtotal:</span>
                <span>${receipt.subtotal?.toFixed(2)}</span>
              </div>
              <div className="row flex justify-between">
                <span>Tax (8%):</span>
                <span>${receipt.tax?.toFixed(2)}</span>
              </div>
              {receipt.tip_amount > 0 && (
                <div className="row flex justify-between">
                  <span>Tip:</span>
                  <span>${receipt.tip_amount?.toFixed(2)}</span>
                </div>
              )}
              {receipt.cash_discount_amount > 0 && (
                <div className="row flex justify-between" data-testid="receipt-cash-discount">
                  <span>{receipt.cash_discount_label || "Non-Cash Adjustment"}:</span>
                  <span>${receipt.cash_discount_amount.toFixed(2)}</span>
                </div>
              )}
              <div className="line border-t border-dashed border-border my-2"></div>
              <div className="row flex justify-between bold font-bold text-sm">
                <span>TOTAL:</span>
                <span>${receipt.total_charged?.toFixed(2)}</span>
              </div>
            </div>

            <div className="line border-t border-dashed border-border my-2"></div>

            {/* Payment Info */}
            <div className="mb-3">
              <div className="row flex justify-between">
                <span>Payment:</span>
                <span className="capitalize">{receipt.payment_method}</span>
              </div>
              {receipt.transaction_id && (
                <div className="row flex justify-between">
                  <span>Trans ID:</span>
                  <span className="text-[10px]">{receipt.transaction_id.slice(0, 16)}</span>
                </div>
              )}
            </div>

            <div className="line border-t border-dashed border-border my-2"></div>

            {/* Footer */}
            <div className="center text-center mt-4">
              <p className="bold font-bold">Thank you!</p>
              <p className="sm text-[10px] text-muted-foreground">Please come again</p>
            </div>

            {/* Loyalty QR — invites non-members to enroll on their way out */}
            {showLoyaltyQr && (
              <>
                <div className="line border-t border-dashed border-border my-3"></div>
                <div className="center text-center" data-testid="receipt-loyalty-qr">
                  <p className="bold font-bold text-[11px] mb-1">Earn rewards on your next visit</p>
                  <p className="sm text-[10px] text-muted-foreground mb-2">
                    Scan to join — 1 point per $1, free perks at higher tiers
                  </p>
                  <div className="flex justify-center my-1">
                    <QRCodeSVG
                      value={loyaltySignupUrl}
                      size={120}
                      level="M"
                      includeMargin={false}
                      bgColor="#FFFDF7"
                      fgColor="#1f1f1f"
                    />
                  </div>
                  {receipt.customer_email && (
                    <p className="sm text-[9px] text-muted-foreground mt-1">
                      Pre-filled with {receipt.customer_email}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Sticky footer with printer picker + spaced-out actions */}
        <div
          className="px-6 py-4 border-t bg-background flex flex-col gap-3 shrink-0"
          data-testid="receipt-footer-actions"
        >
          {hasPrinters && (
            <div className="flex items-center gap-2">
              <Printer size={16} className="text-muted-foreground shrink-0" />
              <Select
                value={selectedPrinterId}
                onValueChange={setSelectedPrinterId}
                disabled={printing || loadingPrinters}
              >
                <SelectTrigger className="h-9" data-testid="receipt-printer-select">
                  <SelectValue placeholder="Select printer" />
                </SelectTrigger>
                <SelectContent>
                  {printers.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`receipt-printer-option-${p.id}`}>
                      {p.name || p.ip_address || p.device_path || "Printer"}
                      {p.status === "online" ? "  •  online" : p.status === "offline" ? "  •  offline" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="gap-2"
              data-testid="exit-receipt-btn"
            >
              <X size={16} />
              Exit
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={handleBrowserPrint}
                className="gap-2"
                title="Open the browser print dialog (PDF / non-thermal printers)"
                data-testid="browser-print-btn"
              >
                <Browser size={16} />
                Browser
              </Button>
              <Button
                onClick={() => handleHardwarePrint(false)}
                disabled={!selectedPrinterId || printing}
                className="gap-2 min-w-[130px]"
                data-testid="print-receipt-btn"
              >
                <Printer size={16} />
                {printing ? "Printing…" : hasPrinters ? "Print" : "No Printer"}
              </Button>
              <Button
                onClick={() => handleHardwarePrint(true)}
                disabled={!selectedPrinterId || printing}
                variant="secondary"
                className="gap-2"
                title="Print the receipt AND pop the cash drawer in one tap — best for cash sales"
                data-testid="print-and-drawer-btn"
              >
                <MoneyWavy size={16} />
                Print + Drawer
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrintableReceipt;
